
import type { GridRow, GridColumn } from '../types/gridTypes';

// --- Types ---

export interface FormulaContext {
  row: GridRow;
  allRows: GridRow[];
  columns: GridColumn[];
  targetColumnId?: string; // Context for relative functions like CHILDREN
}

export interface FormulaResult {
  value: any;
  error?: string;
}

export interface FormulaFunctionMeta {
  name: string;
  category: string;
  description: string;
  signature: string;
  minArgs: number;
  maxArgs?: number;
  examples?: string[];
}

// Updated definition to receive context
export type FormulaFunctionImpl = (args: any[], context: FormulaContext) => any;

export interface RegistryEntry {
  meta: FormulaFunctionMeta;
  impl: FormulaFunctionImpl;
}

// --- Helper: Validation (Preserved) ---

export const validateCell = (value: any, column?: GridColumn): { isValid: boolean; message?: string } => {
  if (!column) return { isValid: true };

  if (column.type === 'number' || column.type === 'currency' || column.type === 'percent') {
      if (typeof value === 'string' && value.trim() !== '' && isNaN(Number(value))) {
          return { isValid: false, message: 'Must be a number' };
      }
  }

  if (column.type === 'select' && column.options) {
      if (typeof value === 'string' && value !== '' && !column.options.includes(value)) {
          return { isValid: false, message: 'Invalid option' };
      }
  }
  
  if (column.allowedValues && !column.allowedValues.includes(value)) {
      return { isValid: false, message: 'Value not allowed' };
  }

  return { isValid: true };
};

// --- Helpers for Implementations ---

const flattenNumericArgs = (args: any[]) => args.flat(Infinity).map(v => {
    if (v === null || v === undefined || v === '') return 0;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
});

// --- Function Registry ---

export const functionRegistry: { [name: string]: RegistryEntry } = {
  SUM: {
    meta: {
      name: 'SUM',
      category: 'Math',
      description: 'Calculates the sum of a list of numbers.',
      signature: 'SUM(value1, [value2, ...])',
      minArgs: 1,
      examples: ['=SUM([Revenue], [Cost])', '=SUM(CHILDREN)']
    },
    impl: (args, _ctx) => flattenNumericArgs(args).reduce((a, b) => a + b, 0)
  },
  AVG: {
    meta: {
      name: 'AVG',
      category: 'Math',
      description: 'Calculates the average of a list of numbers.',
      signature: 'AVG(value1, [value2, ...])',
      minArgs: 1
    },
    impl: (args, _ctx) => {
      const flat = flattenNumericArgs(args);
      return flat.length ? flat.reduce((a, b) => a + b, 0) / flat.length : 0;
    }
  },
  MIN: {
    meta: {
      name: 'MIN',
      category: 'Math',
      description: 'Returns the smallest number in a list.',
      signature: 'MIN(value1, [value2, ...])',
      minArgs: 1
    },
    impl: (args, _ctx) => {
      const flat = flattenNumericArgs(args);
      return flat.length ? Math.min(...flat) : 0;
    }
  },
  MAX: {
    meta: {
      name: 'MAX',
      category: 'Math',
      description: 'Returns the largest number in a list.',
      signature: 'MAX(value1, [value2, ...])',
      minArgs: 1
    },
    impl: (args, _ctx) => {
       const flat = flattenNumericArgs(args);
       return flat.length ? Math.max(...flat) : 0;
    }
  },
  COUNT: {
    meta: {
      name: 'COUNT',
      category: 'Math',
      description: 'Counts the number of items in a list.',
      signature: 'COUNT(value1, [value2, ...])',
      minArgs: 1
    },
    impl: (args, _ctx) => args.flat(Infinity).length
  },
  IF: {
    meta: {
      name: 'IF',
      category: 'Logical',
      description: 'Returns one value if true, another if false.',
      signature: 'IF(condition, value_if_true, value_if_false)',
      minArgs: 3,
      maxArgs: 3
    },
    impl: (args, _ctx) => args[0] ? args[1] : args[2]
  },
  RANGE: {
    meta: {
      name: 'RANGE',
      category: 'Lookup',
      description: 'Returns a range of values from a column between start and end row indices.',
      signature: 'RANGE("Column Label", start_index, end_index)',
      minArgs: 3,
      maxArgs: 3,
      examples: ['=SUM(RANGE("Budget", 1, 5))']
    },
    impl: (args, ctx) => {
      const [colLabel, startArg, endArg] = args;
      if (!colLabel) return [];

      // Find column by label (case-insensitive)
      const col = ctx.columns.find(c => c.label.toLowerCase() === String(colLabel).toLowerCase());
      if (!col) return []; // Return empty if column not found

      // Normalize indices (1-based to 0-based), defaults to full range
      const totalRows = ctx.allRows.length;
      const start = typeof startArg === 'number' ? startArg : 1;
      const end = typeof endArg === 'number' ? endArg : totalRows;

      // Ensure start <= end and valid bounds
      const s = Math.max(1, Math.min(start, end));
      const e = Math.min(Math.max(start, end), totalRows);
      
      // allRows contains the dataset. We slice it.
      // Note: This relies on allRows indices matching the user's visual indices.
      const rowsInRange = ctx.allRows.slice(s - 1, e);

      return rowsInRange.map(r => {
         const val = r.values[col.id];
         if (typeof val === 'number') return val;
         if (typeof val === 'string') {
            const f = parseFloat(val);
            return isNaN(f) ? 0 : f; // Treat non-numeric as 0 for range calcs
         }
         return 0;
      });
    }
  }
};

// --- Metadata Helpers ---

export function getAllFormulaFunctions(): FormulaFunctionMeta[] {
  return Object.values(functionRegistry).map(entry => entry.meta);
}

export function getMatchingFunctions(prefix: string): FormulaFunctionMeta[] {
  const upper = prefix.toUpperCase();
  return Object.values(functionRegistry)
    .filter(e => e.meta.name.startsWith(upper))
    .map(e => e.meta);
}

export function getFunctionMeta(name: string): FormulaFunctionMeta | undefined {
  return functionRegistry[name.toUpperCase()]?.meta;
}

// --- Engine Internals ---

/**
 * Replaces [Column Name] with safe literal values.
 */
export const normalizeFormula = (raw: string, columns: GridColumn[], row?: GridRow): string => {
  let normalized = raw;
  
  // Simple pass to replace [Column Name] with value or 0 if only columns provided (for ID normalization)
  columns.forEach(col => {
    const label = col.label.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`\\[${label}\\]`, 'gi');
    
    if (row) {
        // Execution time: Replace with actual value
        const val = row.values[col.id];
        let safeVal = '0';
        
        if (typeof val === 'number') safeVal = String(val);
        else if (typeof val === 'string') {
           const n = parseFloat(val);
           safeVal = (!isNaN(n) && isFinite(n) && String(n) === val.trim()) ? String(n) : JSON.stringify(val);
        }
        else if (typeof val === 'boolean') safeVal = String(val);
        else if (val === null || val === undefined) safeVal = '0';
        
        normalized = normalized.replace(regex, safeVal);
    } else {
        // Storage time: Replace with ID reference {col:ID}
        normalized = normalized.replace(regex, `{col:${col.id}}`);
    }
  });
  
  return normalized;
};

/**
 * Resolves CHILDREN reference to an array of values for the target column.
 */
const getChildrenValues = (context: FormulaContext): any[] => {
    if (!context.targetColumnId) return [];
    
    const childRows = context.allRows.filter(r => r.parentId === context.row.id);
    return childRows.map(r => {
        const val = r.values[context.targetColumnId!];
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const n = parseFloat(val);
            return isNaN(n) ? 0 : n;
        }
        return 0;
    });
};

export const evaluateFormula = (
  formula: string,
  context: FormulaContext
): FormulaResult => {
  if (!formula.startsWith('=')) {
    return { value: formula };
  }

  let expression = formula.substring(1);

  // Pre-process: Convert aggregation functions on whole columns to RANGE calls
  // e.g. SUM([Duration]) -> SUM(RANGE("Duration"))
  // Matches: FuncName( [Column Name] ) where FuncName is an aggregation
  const aggFuncs = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'];
  aggFuncs.forEach(func => {
      // Regex detects: FUNC([Col]) ignoring case and spaces
      // Group 1: FUNC (not captured by replace if we use explicit name)
      // Group 2: Column Name inside []
      const regex = new RegExp(`${func}\\(\\s*\\[([^\\]]+)\\]\\s*\\)`, 'gi');
      expression = expression.replace(regex, (match, colName) => {
          // Preserve original function name but use RANGE with quoted column name
          // "Duration" -> RANGE("Duration")
          // Escape quotes in column name just in case
          const safeColName = colName.replace(/"/g, '\\"');
          return `${func}(RANGE("${safeColName}"))`;
      });
  });

  try {
    // 1. Normalize Column References [Col] -> Value
    expression = normalizeFormula(expression, context.columns, context.row);

    // 2. Security Check
    if (expression.match(/window|document|fetch|alert|console|import|return|function|eval|exec|constructor/gi)) {
        return { value: '#BLOCKED', error: 'Unsafe expression' };
    }

    // 3. Prepare Registry-based Scope
    const funcNames = Object.keys(functionRegistry);
    
    // Create wrapper functions that bind the current context
    // The "new Function" will call these wrappers with (...args)
    // and the wrappers will call the registry impl with (args, context)
    const funcWrappers = funcNames.map(name => {
        return (...args: any[]) => functionRegistry[name].impl(args, context);
    });
    
    // 4. Handle Children
    // We pass children values as a variable named 'CHILDREN'
    const childrenVal = getChildrenValues(context);

    // 5. Execute
    // The function signature matches keys + CHILDREN
     
    const fn = new Function(...funcNames, 'CHILDREN', `return (${expression})`);
    
    const result = fn(...funcWrappers, childrenVal);

    if (typeof result === 'number') {
        if (!isFinite(result)) return { value: '#ERR', error: 'Infinite' };
        if (isNaN(result)) return { value: '#NAN', error: 'Not a Number' };
    }

    return { value: result };

  } catch (e: any) {
    if (e instanceof SyntaxError) return { value: '#SYNTAX?', error: e.message };
    if (e instanceof ReferenceError) return { value: '#NAME?', error: e.message };
    return { value: '#ERROR', error: String(e) };
  }
};
